/**
 * ProPlusNewRivalry — iter87.199.69
 *
 * Pro+ variant of the shared `/start` onboarding flow. Mounts INSIDE
 * <ProShell> (so ClerkProvider is available), reads the Clerk email
 * as the caller's identity, and hands off to `<Start mode="pro_plus"
 * emailLocked initialEmail=... />` for the actual step flow.
 *
 * Why a separate page instead of query-param branching inside `Start`?
 *   * `useUser()` from Clerk is only valid inside <ClerkProvider>.
 *     Free `/start` never mounts Clerk, so the hook can't sit there.
 *     Wrapping `<Start />` in a Pro+-scoped page keeps the hook call
 *     legal.
 *   * Route separation gives us a clean "you are creating a Pro+
 *     rivalry" URL (`/rivalry-pro/new`) that we can link to from the
 *     hub and share with support without ambiguity.
 *
 * The consent step (T&C + 18+) is intentionally kept — the operator
 * ratified in iter87.199.69 that "every Rivalry entry point re-affirms
 * consent" so we get a fresh audit stamp on every league doc.
 */
import React from "react";
import { useUser } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Start from "@/pages/Start";

export default function ProPlusNewRivalry() {
    const { user, isLoaded } = useUser();
    const navigate = useNavigate();

    if (!isLoaded) {
        return (
            <div
                className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center"
                data-testid="pro-plus-new-rivalry-loading"
            >
                <Loader2 className="w-6 h-6 animate-spin text-cyber" />
            </div>
        );
    }

    // Clerk owns auth state above this component. If user is null
    // (rare — sign-out mid-flow) bounce to the sign-in card. The
    // ProShell also handles this at the route level; belt & braces.
    if (!user) {
        navigate("/rivalry-pro/sign-in", { replace: true });
        return null;
    }

    const email = user.primaryEmailAddress?.emailAddress || "";
    const usernameSeed = (email.split("@")[0] || "admin").slice(0, 40);

    return (
        <Start
            mode="pro_plus"
            initialEmail={email}
            initialUsername={usernameSeed}
            emailLocked
            lockedEmailNote="Locked to your Pro+ account email — this ties the new rivalry to your subscription."
        />
    );
}
